import React, { useContext } from 'react'

import { digObject } from '@domain-group/javascript-utils'

import {
  Box, Button, FileUploadZone, FormField, LoadingModule, ModalContext, SectionDivider, SidebarModal,
} from '@domain-group/suit-theme'

import useAttachments from '@hooks/useAttachments'
import useUppyAttachmentUpload from '@hooks/useUppyAttachmentUpload'

import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'

import AttachmentListItem from '@components/AttachmentListItem'

const MODAL_KEY = 'UploadEntityAssetsModal'

type ModalCallbacks = {
  closeModal: () => void,
  createEntityName: (payload: HandleCallbackActionParams) => void,
  deleteAttachment: (payload: HandleCallbackActionParams) => void,
}

type ModalProps = {
  callbacks: ModalCallbacks,
  showModal: boolean,
}

const UploadEntityAssetsModal = (props: ModalProps) => {
  const {
    callbacks,
    showModal,
  } = props

  const { closeModal, deleteAttachment } = callbacks

  const modalContext = useContext(ModalContext)
  const { modalData } = modalContext

  const modalPayload = digObject(modalData, MODAL_KEY, {})
  const { attachment, hideUploadZone = false } = modalPayload

  const { attachable_id, attachable_type } = attachment || {}

  const attachmentsPayload = useAttachments({
    filters: attachment,
    performHttpRequests: !!attachable_type && !!attachable_id,
  })

  const {
    filteredAttachments,
    hasFilteredAttachments,
    loading,
  } = attachmentsPayload

  const uppy = useUppyAttachmentUpload({ attachmentParams: attachment }, [...Object.values(attachment)])

  return (
    <SidebarModal callbacks={callbacks} clickSafeZone modalKey={MODAL_KEY} showModal={showModal} size="small">
      <SidebarModal.Header callbacks={callbacks} title="Upload" titleSecondLine="Assets" />

      <SidebarModal.Content>
        {!hideUploadZone && (
          <FormField label="* Upload Assets">
            <FileUploadZone uppy={uppy} />
          </FormField>
        )}

        <LoadingModule boxProps={{ marginTop: 'large' }} loading={loading} />

        {!loading && hasFilteredAttachments && (
          <>
            <SectionDivider marginTop={hideUploadZone ? 0 : 'large'}>Uploaded Assets</SectionDivider>

            <Box flexDirection="column">
              {filteredAttachments.map(item => (
                <AttachmentListItem
                  attachment={item}
                  boxProps={{ paddingX: 0 }}
                  callbacks={{ deleteAttachment }}
                  key={item.id}
                />
              ))}
            </Box>
          </>
        )}
      </SidebarModal.Content>

      <SidebarModal.Footer>
        <Button buttonStyle="secondaryUtility" onClick={() => closeModal()} size="large">
          Close
        </Button>
      </SidebarModal.Footer>
    </SidebarModal>
  )
}

const LazyLoadedModal = (props: ModalProps) => (
  <SidebarModal.RenderController {...props}>
    <UploadEntityAssetsModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
